/* eslint-disable */
import { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import { toast } from 'react-toastify';
import { getNotifications, markNotificationAsRead } from '../../service/notifications.service';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
    minHeight: 400,
  },
  list: {
    width: '100%',
    padding: 0,
  },
  listItem: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(8),
    '&.unread': {
      backgroundColor: '#fff8f0',
    },
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  readBadge: {
    fontSize: '0.72rem',
    color: '#999',
    fontStyle: 'italic',
  },
  emptyBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 350,
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 400,
  },
  scrollableContent: {
    maxHeight: 400,
    overflowY: 'auto',
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function NotificationsModal({ open, setOpen }) {
  const classes = useStyles();
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [lastPage, setLastPage] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const fetchNotifications = async (pageToLoad = 1, append = false) => {
    if (lastPage)
      return false;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const data = await getNotifications(pageToLoad);
      const nextNotifications = data?.data ?? [];
     
      if (data.to == data.total) {
        setLastPage(true);
        toast.info('Todas as notificações foram carregadas.');
      }

      setLastPage(false);
      setNotifications((prev) => {
        if (!append) {
          return nextNotifications;
        }

        const mergedNotifications = [...prev, ...nextNotifications].filter(
          (notification, index, list) =>
            index === list.findIndex((current) => current.id === notification.id)
        );

        return mergedNotifications;
      });
      return true;
    } catch (err) {
        toast.error(`Erro ao carregar notificações. ${err.message}`, { autoClose: 10000 });
      return false;
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open) {
      setPage(1);
      setHasMoreNotifications(true);
      fetchNotifications(1);
    }
  }, [open]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    const loaded = await fetchNotifications(nextPage, true);

    if (loaded) {
      setPage(nextPage);
    }
  };

  const handleMarkAsRead = async (notification) => {
    if (notification.readingStatus) return;
    setMarkingId(notification.id);
    try {
      await markNotificationAsRead(notification.id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, readingStatus: true, notificationRead: new Date() } : n
        )
      );
      toast.success('Notificação marcada como lida.');
    } catch (err) {
      toast.error('Erro ao marcar notificação como lida.');
    } finally {
      setMarkingId(null);
    }
  };

  const isRead = (n) => n.readingStatus;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="notifications-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="notifications-dialog-title" style={{ backgroundColor: '#fc986a' }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Notificações
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={32} style={{ color: '#fc986a' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box className={classes.emptyBox}>
            <Typography variant="body2" color="textSecondary">
              Nenhuma notificação encontrada.
            </Typography>
          </Box>
        ) : (
          <div className={classes.scrollableContent}>
            <List className={classes.list} disablePadding>
              {notifications?.map((notification, index) => (
              <div key={notification.id}>
                <ListItem
                  className={`${classes.listItem} ${!isRead(notification) ? 'unread' : ''}`}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        style={{ fontWeight: isRead(notification) ? 400 : 600 }}
                      >
                        {notification.title ?? '(sem título)'}
                      </Typography>
                    }
                    secondary={
                      <>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <Typography
                            component="span"
                            variant="body2"
                            color="textPrimary"
                            dangerouslySetInnerHTML={{ __html: notification.content ?? '' }}
                          />
                        </div>
                        <br />
                        <span className={classes.date}>
                          <span className={classes.readBadge}>· Enviada em: </span>
                          {formatDate(notification.notificationDate)}
                        </span>
                        <br/>
                        {isRead(notification) && (
                          <>
                            {' '}
                            <span className={classes.readBadge}>· lida </span>
                          </>
                        )}
                        <span className={classes.date}>
                          {formatDate(notification.notificationRead)}
                        </span>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {!isRead(notification) && (
                      <Tooltip title="Marcar como lida">
                        <span>
                          <IconButton
                            edge="end"
                            aria-label="marcar como lida"
                            onClick={() => handleMarkAsRead(notification)}
                            disabled={markingId === notification.id}
                            size="small"
                          >
                            {markingId === notification.id ? (
                              <CircularProgress size={18} style={{ color: '#fc986a' }} />
                            ) : (
                              <DoneIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {isRead(notification) && (
                      <DoneAllIcon fontSize="small" style={{ color: '#bbb', marginRight: 4 }} />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                {index < notifications.length - 1 && <Divider component="li" />}
              </div>
            ))}
            </List>

            <Box display="flex" justifyContent="center" width="100%" py={2}>
              <Button
                onClick={handleLoadMore}
                color="primary"
                disabled={loading || loadingMore || !hasMoreNotifications}
              >
                {loadingMore ? 'Carregando...' : 'Mais Notificações'}
              </Button>
            </Box>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
